import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Toolbar from '@mui/material/Toolbar';
import { doc, getDoc } from 'firebase/firestore';

import PageTemplate from '../../components/PageTemplate';
import { ListingDetail } from '../../components/ListingDetail';
import { useApp } from '../../../context';
import { db } from '../../../config/firebase';

/**
 * /listing/:id — one listing, at its own URL.
 *
 * Replaces the fullScreen dialog that `CardSection`, `HomeCardSection` and the
 * map popups used to open. A listing can now be linked, shared, bookmarked,
 * opened in a new tab, and pointed at by an advert.
 */
export default function Listing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceListing, listingsLoaded } = useApp();

  // The published listings already streamed into context — no read needed for
  // the overwhelmingly common case (a traveller tapping a card in the feed).
  const cached = useMemo(
    () => (serviceListing || []).find((l) => l.id === id) || null,
    [serviceListing, id]
  );

  // Only for a cold deep link that lands before, or outside, the snapshot.
  const [fetched, setFetched] = useState(null);
  const [fetchDone, setFetchDone] = useState(false);

  useEffect(() => {
    // Reset when the id changes, or a second listing would briefly show the
    // first one's fetched document.
    setFetched(null);
    setFetchDone(false);

    // Wait for the snapshot before concluding anything. `serviceListing` is []
    // for the first frames of a cold load, so acting on "not in the cache"
    // here would 404 every deep link before flipping to content.
    if (!listingsLoaded || cached || !id) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'listings', id));
        if (cancelled) return;
        // `listings` is world-readable, so this read also succeeds for DRAFT
        // and ARCHIVED rows. Only PUBLISHED ones are public content — anything
        // else must 404 rather than become a linkable preview of unpublished
        // work.
        const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setFetched(data && data.status === 'PUBLISHED' ? data : null);
      } catch (error) {
        console.error('Listing: failed to read listing', id, error);
        if (!cancelled) setFetched(null);
      } finally {
        if (!cancelled) setFetchDone(true);
      }
    })();

    return () => { cancelled = true; };
  }, [id, cached, listingsLoaded]);

  const listing = cached || fetched;
  const resolving = !listingsLoaded || (!cached && !fetchDone);

  // An advert or a shared link can land here with no history entry of ours;
  // navigate(-1) would then take the traveller off the site entirely.
  const handleBack = () => {
    if (location.key && location.key !== 'default') navigate(-1);
    else navigate('/');
  };

  if (resolving) {
    return (
      <PageTemplate>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress aria-label="Loading listing" />
        </Box>
      </PageTemplate>
    );
  }

  if (!listing) {
    return (
      <PageTemplate>
        <Box sx={{ textAlign: 'center', px: 3, py: 10 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            We couldn&apos;t find that listing
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            It may have been removed, or the link may be incomplete.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Back to Vhakacha
          </Button>
        </Box>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      {/* Clears the fixed app bar, as every other page does. The old detail
          view got away without it: its Back button was absolutely positioned
          over the photo carousel, so nothing sat in the occluded strip. The
          masthead does, and the Back button was landing underneath the bar. */}
      <Toolbar />
      <ListingDetail listing={listing} onBack={handleBack} />
    </PageTemplate>
  );
}
